import { type Card, rankOf, suitOf } from './cards.js';

export const CATEGORY_NAMES = [
  'High Card', 'Pair', 'Two Pair', 'Three of a Kind', 'Straight',
  'Flush', 'Full House', 'Four of a Kind', 'Straight Flush',
];

const BASE = 13;
const CATEGORY_UNIT = BASE ** 5;

function pack(category: number, tiebreaks: number[]): number {
  let v = category;
  for (let i = 0; i < 5; i++) v = v * BASE + (tiebreaks[i] ?? 0);
  return v;
}

export function categoryOf(strength: number): number {
  return Math.floor(strength / CATEGORY_UNIT);
}

export function evaluate5(cards: Card[]): number {
  if (cards.length !== 5) throw new Error(`evaluate5 requires exactly 5 cards, got ${cards.length}`);
  const ranks = cards.map(rankOf).sort((a, b) => b - a);
  const isFlush = cards.every(c => suitOf(c) === suitOf(cards[0]!));

  const counts = new Map<number, number>();
  for (const r of ranks) counts.set(r, (counts.get(r) ?? 0) + 1);
  // groups: by count desc, then rank desc — e.g. full house KKK22 -> [[K,3],[2,2]]
  const groups = [...counts.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]);
  const unique = [...counts.keys()].sort((a, b) => b - a);

  let straightHigh = -1;
  if (unique.length === 5) {
    if (unique[0]! - unique[4]! === 4) straightHigh = unique[0]!;
    else if (unique[0] === 12 && unique[1] === 3) straightHigh = 3; // wheel: A,5,4,3,2 -> 5-high
  }

  if (isFlush && straightHigh >= 0) return pack(8, [straightHigh]);
  if (groups[0]![1] === 4) return pack(7, [groups[0]![0], groups[1]![0]]);
  if (groups[0]![1] === 3 && groups[1]![1] === 2) return pack(6, [groups[0]![0], groups[1]![0]]);
  if (isFlush) return pack(5, ranks);
  if (straightHigh >= 0) return pack(4, [straightHigh]);
  if (groups[0]![1] === 3) return pack(3, [groups[0]![0], groups[1]![0], groups[2]![0]]);
  if (groups[0]![1] === 2 && groups[1]![1] === 2) {
    return pack(2, [groups[0]![0], groups[1]![0], groups[2]![0]]);
  }
  if (groups[0]![1] === 2) return pack(1, [groups[0]![0], groups[1]![0], groups[2]![0], groups[3]![0]]);
  return pack(0, ranks);
}

export function evaluateBest5of7(cards: Card[]): number {
  if (cards.length !== 7) throw new Error(`evaluateBest5of7 requires exactly 7 cards, got ${cards.length}`);
  let best = -1;
  for (let drop1 = 0; drop1 < 6; drop1++) {
    for (let drop2 = drop1 + 1; drop2 < 7; drop2++) {
      const five: Card[] = [];
      for (let i = 0; i < 7; i++) if (i !== drop1 && i !== drop2) five.push(cards[i]!);
      const v = evaluate5(five);
      if (v > best) best = v;
    }
  }
  return best;
}

/** Best five-card strength of 5, 6, or 7 cards. */
export function evaluateBest(cards: Card[]): number {
  if (cards.length === 5) return evaluate5(cards);
  if (cards.length === 7) return evaluateBest5of7(cards);
  if (cards.length !== 6) throw new Error(`evaluateBest requires 5-7 cards, got ${cards.length}`);
  let best = -1;
  for (let drop = 0; drop < 6; drop++) {
    const v = evaluate5(cards.filter((_, i) => i !== drop));
    if (v > best) best = v;
  }
  return best;
}
